import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Check, X, Upload, Globe, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

export function isBareDomainUrl(url: string | null | undefined): boolean {
  if (!url || !url.trim()) return true;
  const trimmed = url.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const parsed = new URL(trimmed);
      const ext = parsed.pathname.split(".").pop()?.toLowerCase() || "";
      if (["png", "jpg", "jpeg", "svg", "gif", "webp", "ico"].includes(ext)) return false;
      if (parsed.pathname === "/" || parsed.pathname === "") return true;
      return false;
    } catch {
      return true;
    }
  }
  if (trimmed.includes("/") && trimmed.split("/").pop()?.includes(".")) return false;
  return true;
}

interface ExtractedBrand {
  logoUrl: string | null;
  brandColors: string[];
  tagline?: string;
  fontFamily?: string;
  brandPersonality?: string;
}

interface BrandKitPullerProps {
  workspaceId: number | null;
  websiteUrl?: string;
  extractUrl?: string;
  onApply: (data: {
    logoUrl?: string;
    brandColors?: string[];
    tagline?: string;
    fontFamily?: string;
  }) => void;
  onSkip?: () => void;
  autoExtract?: boolean;
}

export function BrandKitPuller({ workspaceId, websiteUrl, extractUrl, onApply, onSkip, autoExtract }: BrandKitPullerProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedBrand | null>(null);
  const [nothingFound, setNothingFound] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualLogoUrl, setManualLogoUrl] = useState("");
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [manualColor, setManualColor] = useState("#3B82F6");
  const [manualColors, setManualColors] = useState<string[]>([]);
  const [didAutoExtract, setDidAutoExtract] = useState(false);

  const canExtract = !!(workspaceId || extractUrl);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (autoExtract && canExtract && websiteUrl && !didAutoExtract && !extracted && !extracting) {
      setDidAutoExtract(true);
      handleExtract();
    }
  }, [autoExtract, canExtract, websiteUrl]);

  async function handleExtract() {
    if (!canExtract) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setExtracting(true);
    setExtracted(null);
    setNothingFound(false);
    try {
      let res: Response;
      if (workspaceId) {
        res = await fetch(`/api/workspaces/${workspaceId}/extract-brand`, {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
        });
      } else {
        const url = extractUrl || websiteUrl || "";
        res = await fetch("/api/extract-brand-url", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
          signal: controller.signal,
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Extraction failed", description: err.message || "Could not extract brand assets.", variant: "destructive" });
        setNothingFound(true);
        return;
      }
      const data = await res.json();
      if (!data.logoUrl && (!data.brandColors || data.brandColors.length === 0)) {
        setNothingFound(true);
        toast({ title: "No brand assets found", description: "We couldn't find a logo or colors from your website. You can upload them manually below." });
        return;
      }
      setExtracted(data);
      const extras = [data.tagline && `"${data.tagline}"`, data.fontFamily && `Font: ${data.fontFamily}`].filter(Boolean).join(" · ");
      toast({ title: "Brand assets found", description: extras || "Logo and colors extracted from your website." });
    } catch (e: unknown) {
      if (e instanceof Error && e.name === "AbortError") return;
      toast({ title: "Error", description: "Failed to contact the server.", variant: "destructive" });
      setNothingFound(true);
    } finally {
      setExtracting(false);
    }
  }

  function handleApply() {
    if (!extracted) return;
    onApply({
      logoUrl: extracted.logoUrl && !isBareDomainUrl(extracted.logoUrl) ? extracted.logoUrl : undefined,
      brandColors: extracted.brandColors?.length > 0 ? extracted.brandColors : undefined,
      tagline: extracted.tagline || undefined,
      fontFamily: extracted.fontFamily || undefined,
    });
    setExtracted(null);
    toast({ title: "Applied", description: "Brand assets applied." });
  }

  async function handleFileUpload(file: File) {
    if (!workspaceId) return;
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch(`/api/workspaces/${workspaceId}/assets`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: file.name, url: dataUrl, type: "image", size: file.size }),
      });
      if (!res.ok) throw new Error("Upload failed");
      const asset = await res.json();
      const assetUrl = asset.url || dataUrl;
      setManualLogoUrl(assetUrl);
      toast({ title: "Logo uploaded", description: "Logo saved to your assets library." });
    } catch {
      toast({ title: "Upload failed", description: "Could not upload logo.", variant: "destructive" });
    } finally {
      setUploadingLogo(false);
    }
  }

  function handleManualApply() {
    const data: Parameters<typeof onApply>[0] = {};
    if (manualLogoUrl.trim() && !isBareDomainUrl(manualLogoUrl)) data.logoUrl = manualLogoUrl.trim();
    if (manualColors.length > 0) data.brandColors = manualColors;
    if (Object.keys(data).length === 0) {
      toast({ title: "Nothing to apply", description: "Add a logo or at least one color.", variant: "destructive" });
      return;
    }
    onApply(data);
    setShowManual(false);
    toast({ title: "Applied", description: "Brand assets applied." });
  }

  function addManualColor() {
    if (manualColor && !manualColors.includes(manualColor)) {
      setManualColors([...manualColors, manualColor]);
    }
  }

  return (
    <div className="space-y-3" data-testid="brand-kit-puller">
      {!extracted && !extracting && (
        <div className="space-y-3">
          {canExtract ? (
            <Button
              type="button"
              onClick={handleExtract}
              disabled={extracting}
              data-testid="button-pull-brand-kit"
              className="w-full gap-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/30"
              variant="outline"
            >
              <Globe size={14} />
              Pull Brand Kit from {websiteUrl || extractUrl || "your website"}
            </Button>
          ) : websiteUrl ? (
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl" data-testid="panel-auto-extract-info">
              <p className="text-xs text-blue-400 font-medium">Brand assets will be extracted automatically from {websiteUrl} when you launch</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Or add them manually below.</p>
            </div>
          ) : null}

          {nothingFound && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl" data-testid="panel-nothing-found">
              <p className="text-xs text-amber-400 font-medium">No brand assets found on the website</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Upload your logo and pick your colors manually below, or skip for now.
              </p>
            </div>
          )}
        </div>
      )}

      {extracting && (
        <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl" data-testid="panel-extracting">
          <Loader2 size={16} className="animate-spin text-blue-400" />
          <div>
            <p className="text-sm font-medium text-blue-400">Scanning {websiteUrl || extractUrl || "website"}...</p>
            <p className="text-xs text-muted-foreground/70">Looking for logo, brand colors, and tagline</p>
          </div>
        </div>
      )}

      {extracted && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl space-y-3" data-testid="panel-extracted-brand">
          <p className="text-xs font-bold text-blue-400 uppercase tracking-wider">Found on your website</p>
          <div className="flex items-center gap-4 flex-wrap">
            {extracted.logoUrl && !isBareDomainUrl(extracted.logoUrl) && (
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 rounded-lg border border-border bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
                  <img src={extracted.logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
                <div className="flex items-center gap-1 text-xs text-emerald-400">
                  <Check size={12} /> Logo
                </div>
              </div>
            )}
            {extracted.brandColors && extracted.brandColors.length > 0 && (
              <div className="flex items-center gap-2">
                {extracted.brandColors.map(c => (
                  <div key={c} className="w-8 h-8 rounded-lg border border-border shadow-sm" style={{ backgroundColor: c }} title={c} />
                ))}
                <div className="flex items-center gap-1 text-xs text-emerald-400">
                  <Check size={12} /> Colors
                </div>
              </div>
            )}
          </div>
          {extracted.tagline && (
            <p className="text-xs text-muted-foreground italic">"{extracted.tagline}"</p>
          )}
          {extracted.fontFamily && (
            <p className="text-xs text-muted-foreground">Font: <span className="font-medium text-foreground">{extracted.fontFamily}</span></p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button type="button" onClick={handleApply} size="sm" data-testid="button-apply-extracted" className="gap-1.5 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-600/30" variant="outline">
              <Check size={12} /> Use these
            </Button>
            <Button type="button" onClick={() => setExtracted(null)} size="sm" variant="ghost" data-testid="button-dismiss-extracted" className="text-muted-foreground">
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {!extracting && !extracted && (
        <>
          <button
            type="button"
            onClick={() => setShowManual(v => !v)}
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="toggle-manual-brand"
          >
            {showManual ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {showManual ? "Hide manual entry" : "Or add brand assets manually"}
          </button>

          {showManual && (
            <div className="p-4 bg-muted/20 border border-border rounded-xl space-y-4" data-testid="panel-manual-brand">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Logo</label>
                {workspaceId ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="button-upload-logo-file"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs font-semibold rounded-lg hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                      >
                        {uploadingLogo ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                        {uploadingLogo ? "Uploading..." : "Upload Logo File"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowUrlInput(v => !v)}
                        className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors underline underline-offset-2"
                      >
                        or enter URL
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                        e.target.value = "";
                      }}
                    />
                    {showUrlInput && (
                      <input
                        type="url"
                        data-testid="input-manual-logo-url"
                        className="w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                        placeholder="https://yourdomain.com/logo.png"
                        value={manualLogoUrl}
                        onChange={(e) => setManualLogoUrl(e.target.value)}
                      />
                    )}
                    {manualLogoUrl && !isBareDomainUrl(manualLogoUrl) && (
                      <img src={manualLogoUrl} alt="Logo" className="mt-1 h-8 max-w-[120px] object-contain rounded bg-white/5 border border-border p-1" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      type="url"
                      data-testid="input-manual-logo-url"
                      className="w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                      placeholder="https://yourdomain.com/logo.png"
                      value={manualLogoUrl}
                      onChange={(e) => setManualLogoUrl(e.target.value)}
                    />
                    {manualLogoUrl && !isBareDomainUrl(manualLogoUrl) && (
                      <img src={manualLogoUrl} alt="Logo" className="mt-2 h-8 max-w-[120px] object-contain rounded bg-white/5 border border-border p-1" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Brand Colors</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {manualColors.map(c => (
                    <div key={c} className="flex items-center gap-1.5 bg-muted/50 border border-border rounded-lg px-2 py-1">
                      <div className="w-5 h-5 rounded-md border border-border" style={{ backgroundColor: c }} />
                      <span className="text-xs text-muted-foreground font-mono">{c}</span>
                      <button type="button" onClick={() => setManualColors(manualColors.filter(x => x !== c))} className="text-muted-foreground/50 hover:text-muted-foreground">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input type="color" value={manualColor} onChange={(e) => setManualColor(e.target.value)} className="w-8 h-8 rounded border border-border cursor-pointer bg-transparent" data-testid="input-manual-color-picker" />
                  <Button type="button" onClick={addManualColor} size="sm" variant="outline" className="text-xs" data-testid="button-add-manual-color">Add Color</Button>
                </div>
              </div>
              <Button type="button" onClick={handleManualApply} size="sm" className="gap-1.5" data-testid="button-apply-manual-brand">
                <Check size={12} /> Apply
              </Button>
            </div>
          )}

          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors underline underline-offset-2"
              data-testid="button-skip-brand-kit"
            >
              Skip for now
            </button>
          )}
        </>
      )}
    </div>
  );
}
